<template>
  <div>
    <!-- 1. Dashboard: The Next Holiday (Mobile Optimized) -->
    <v-card v-if="nextHoliday" class="mb-6 overflow-hidden" elevation="2" border rounded="xl">
      <v-row no-gutters>
        <!-- Left Side: Info -->
        <v-col cols="12" sm="8" class="pa-4 pa-sm-6 bg-red-lighten-5">
          <div class="text-overline text-red-darken-2 font-weight-black mb-1">NEXT HOLIDAY</div>
          <div class="text-h4 text-sm-h3 font-weight-black text-red-darken-4 mb-1 mb-sm-2">
            {{ calculateDDay(nextHoliday.startDate) }}
          </div>
          <div class="text-subtitle-1 text-sm-h5 font-weight-bold text-grey-darken-4 mb-1 mb-sm-2">
            {{ nextHoliday.title }}
          </div>
          <div class="d-flex align-center text-caption text-sm-subtitle-1 text-red-darken-1 font-weight-medium">
            <v-icon icon="mdi-calendar-check" size="x-small" class="mr-1 mr-sm-2"></v-icon>
            {{ formatDateSimple(nextHoliday.startDate) }}
            <span v-if="nextHoliday.totalDays > 1" class="mx-1">~</span>
            <span v-if="nextHoliday.totalDays > 1">{{ formatDateSimple(nextHoliday.endDate) }}</span>
          </div>
        </v-col>

        <!-- Right Side: Duration Badge (Mobile Optimized) -->
        <v-col cols="12" sm="4" class="d-flex align-center justify-center pa-4 pa-sm-6 bg-white border-sm-left">
          <div v-if="nextHoliday.totalDays > 1" class="d-flex flex-row flex-sm-column align-center">
            <v-avatar color="red-lighten-5" size="50" size-sm="80" class="mr-4 mr-sm-0 mb-sm-2">
              <v-icon icon="mdi-palm-tree" color="red-darken-2" size="24" size-sm="40"></v-icon>
            </v-avatar>
            <div class="text-center">
              <div class="d-flex align-baseline justify-center">
                <span class="text-h4 text-sm-h3 font-weight-black text-red-darken-2">{{ nextHoliday.totalDays }}</span>
                <span class="text-body-2 text-sm-h6 font-weight-bold text-grey-darken-1 ml-1">일간</span>
              </div>
              <v-chip color="red-darken-2" variant="flat" size="x-small" size-sm="small" class="mt-1 font-weight-bold px-3 px-sm-4">
                황금 연휴!
              </v-chip>
            </div>
          </div>
          <div v-else class="d-flex flex-row flex-sm-column align-center">
            <v-avatar color="grey-lighten-4" size="50" size-sm="80" class="mr-4 mr-sm-0 mb-sm-2">
              <v-icon icon="mdi-calendar-heart" color="red-lighten-1" size="24" size-sm="40"></v-icon>
            </v-avatar>
            <div class="text-subtitle-2 text-sm-h6 font-weight-bold text-grey-darken-1">달콤한 휴식</div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-divider class="my-6"></v-divider>

    <!-- 2. Integrated Holiday List -->
    <div class="d-flex align-center mb-4">
      <v-icon icon="mdi-calendar-star" color="red-lighten-1" class="mr-2"></v-icon>
      <h3 class="text-h5 font-weight-bold text-grey-darken-3">공휴일 및 연휴 일정</h3>
    </div>
    
    <v-row>
      <v-col 
        v-for="group in allHolidayGroups" 
        :key="group.id" 
        cols="12" 
        sm="6"
        md="4"
      >
        <v-card 
          class="h-100" 
          hover 
          elevation="1"
          @click="toggleGroup(group.id)"
          :variant="selectedGroupId === group.id ? 'outlined' : 'elevated'"
          rounded="lg"
          style="border-top: 4px solid;"
          :style="{ borderTopColor: getLabelColorHex(group.totalDays) }"
        >
          <v-card-item>
            <div class="d-flex justify-space-between align-start mb-2">
              <v-chip 
                :color="getLabelColor(group.totalDays)" 
                variant="tonal" 
                size="small" 
                label
                class="font-weight-bold"
              >
                {{ getLabelText(group.totalDays) }}
              </v-chip>
              <span class="text-subtitle-1 font-weight-bold" :class="'text-' + getLabelColor(group.totalDays)">
                {{ calculateDDay(group.startDate) }}
              </span>
            </div>
            
            <v-card-title class="text-h6 font-weight-bold pb-1 text-wrap" style="word-break: keep-all; line-height: 1.2;">
              {{ group.title }}
            </v-card-title>
            
            <div class="d-flex align-center mt-2 text-caption text-medium-emphasis font-weight-medium">
              <v-icon icon="mdi-calendar-range" size="x-small" class="mr-1"></v-icon>
              <span>{{ formatDateSimple(group.startDate) }}</span>
              <span v-if="group.totalDays > 1"> ~ {{ formatDateSimple(group.endDate) }}</span>
              <v-spacer></v-spacer>
              <span class="text-primary font-weight-black">총 {{ group.totalDays }}일</span>
            </div>
          </v-card-item>

          <v-expand-transition>
            <div v-if="selectedGroupId === group.id">
              <v-divider></v-divider>
              <v-card-text class="bg-grey-lighten-5 pa-3">
                <div 
                  v-for="(day, index) in group.days" 
                  :key="index"
                  class="d-flex align-center mb-2"
                >
                  <span 
                    class="text-caption font-weight-bold mr-3 text-no-wrap" 
                    :class="getDayColorClass(day)"
                    style="min-width: 100px;"
                  >
                    {{ formatDateSimple(day.date) }}
                  </span>
                  <span class="text-body-2 text-wrap text-grey-darken-3">
                    {{ day.title }}
                  </span>
                </div>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="allHolidayGroups.length === 0" type="info" variant="tonal" class="mt-4" rounded="lg">
      남은 공휴일 일정이 없습니다.
    </v-alert>

  </div>
</template>

<script>
import { events } from '../data/events';
import { calculateDDay } from '../assets/globalFx';

export default {
  name: 'HolidayPage',
  data() {
    return {
      selectedGroupId: null,
      events: events
    }
  },
  computed: {
    allHolidayGroups() {
      const today = new Date();
      today.setHours(0,0,0,0);
      const endLimit = new Date('2027-12-31');

      const holidaysMap = new Map();
      let cur = new Date(today);
      while (cur <= endLimit) {
        const year = cur.getFullYear();
        const month = String(cur.getMonth() + 1).padStart(2, '0');
        const day = String(cur.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const dayOfWeek = cur.getDay();
        const event = this.events.find(e => e.date === dateStr && (e.categoryId === 'holiday' || e.categoryId === 'newyear'));

        if (event) {
          holidaysMap.set(dateStr, { type: 'holiday', title: event.title });
        } else if (dayOfWeek === 0) { 
          holidaysMap.set(dateStr, { type: 'weekend', title: '일요일' });
        } else if (dayOfWeek === 6) { 
          holidaysMap.set(dateStr, { type: 'weekend', title: '토요일' });
        }
        cur.setDate(cur.getDate() + 1);
      }

      const groups = [];
      let currentGroup = [];
      const sortedDates = Array.from(holidaysMap.keys()).sort();

      for (let i = 0; i < sortedDates.length; i++) {
        const dateStr = sortedDates[i];
        const dayInfo = { date: dateStr, ...holidaysMap.get(dateStr) };
        if (currentGroup.length === 0) {
          currentGroup.push(dayInfo);
        } else {
          const lastDate = new Date(currentGroup[currentGroup.length - 1].date);
          const thisDate = new Date(dateStr);
          lastDate.setHours(0,0,0,0);
          thisDate.setHours(0,0,0,0);
          const diff = Math.round((thisDate - lastDate) / (1000 * 60 * 60 * 24));
          if (diff === 1) currentGroup.push(dayInfo);
          else { groups.push(currentGroup); currentGroup = [dayInfo]; }
        }
      }
      if (currentGroup.length > 0) groups.push(currentGroup);

      return groups
        .filter(group => group.some(day => day.type === 'holiday'))
        .map((group, idx) => {
          const startDate = group[0].date;
          const endDate = group[group.length - 1].date;
          const totalDays = group.length;
          const holidayNames = [...new Set(group.filter(d => d.type === 'holiday').map(d => d.title))];
          let title = holidayNames.join(', ');
          
          if (totalDays >= 5) title += ' 황금연휴';
          else if (totalDays >= 3) title += ' 연휴';

          return {
            id: `group-${idx}`,
            title: title,
            startDate: startDate,
            endDate: endDate,
            totalDays: totalDays,
            days: group
          };
        });
    },
    nextHoliday() {
        return this.allHolidayGroups.length > 0 ? this.allHolidayGroups[0] : null;
    }
  },
  methods: {
    calculateDDay,
    toggleGroup(id) {
      this.selectedGroupId = this.selectedGroupId === id ? null : id;
    },
    formatDateSimple(dateString) {
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return `${date.getFullYear()}년 ${date.getMonth() + 1}/${date.getDate()}(${days[date.getDay()]})`;
    },
    getLabelColor(days) {
        if (days >= 5) return 'amber-darken-1'; 
        if (days >= 3) return 'green-lighten-1'; 
        return 'red-lighten-1'; 
    },
    getLabelColorHex(days) {
        if (days >= 5) return '#FFB300'; 
        if (days >= 3) return '#81C784'; 
        return '#EF5350'; 
    },
    getLabelText(days) {
        if (days >= 5) return '황금연휴';
        if (days >= 3) return '연휴';
        return '공휴일';
    },
    getDayColorClass(day) {
        if (day.type === 'holiday') return 'text-red-darken-1';
        if (day.type === 'weekend') return 'text-orange-darken-1';
        return 'text-medium-emphasis';
    }
  }
}
</script>